/**
 * F181 — PDF 쪽수를 **업로드 없이** 브라우저에서 센다 (2026-08-06, 사장님 지시).
 *
 * 왜 자체 경량 판독인가 (사장님 결정 2026-08-06):
 *   pdf.js 는 정확하지만 의존성 +1 · 번들 +수백 KB 다. 이 숫자는 **표시용**
 *   이고 차감의 정본은 서버 pypdf(`pricing.pdf_page_count`)이므로, 여기서는
 *   의존성 0 으로 읽되 **못 읽으면 틀린 숫자 대신 null** 을 돌려준다 —
 *   화면은 null 이면 "쪽수 미확인 — 서버 기준 차감" 확인 단계로 간다.
 *
 * 어떻게 세나 (앞이 못 하면 뒤가 받는다):
 *   ① `/Type /Pages` 객체의 `/Count N` — 페이지 트리 **루트**의 Count 가
 *      전체 쪽수다. 중간 노드의 Count 는 더 작으므로 **최댓값**을 취한다.
 *   ② 선형화 사전(`/Linearized … /N n`)의 `/N` — 전체 쪽수를 뜻한다.
 *   ③ `/Type /Page` 낱장 세기(①·② 가 없을 때만) — `\b` 가 `Pages` 를
 *      제외한다('e'와 's' 사이는 단어 경계가 아니다).
 *   ④ ①~③ 이 다 비면 **압축 객체 스트림(/Type /ObjStm, PDF 1.5+)** 을
 *      브라우저 내장 `DecompressionStream('deflate')` 로 풀어 그 안을 ①·③
 *      과 같은 규칙으로 훑는다 — pdflatex 등 최신 생성기는 페이지 트리를
 *      전부 여기 넣는다(실측: 복합표본 D·G·math1 이 이 단계에서만 읽힌다).
 *      DecompressionStream 이 없는 낡은 브라우저는 그냥 null(= 미확인).
 *
 * ★ 함정 5(검증 안 된 기능 금지) 대응: 이 파일은 **순수 함수**다 — DOM·
 *   Tiptap 의존 0. tests/test_dox_offline.py 가 node 표(합성 PDF 7종)로
 *   **돌려서** 확인하고, 실물 대조(pypdf 와 동일값)는 VERIFY 9-88 에 기록.
 *
 * ★ 한계(기록): 증분 갱신으로 남은 옛 `/Pages` 객체의 Count 가 더 크면
 *   과대 표시될 수 있다(희귀). 확인 문구가 "서버 기준 차감"을 함께 말한다.
 */

/** 바이트를 latin1 문자열로 — 1바이트 = 1문자라 **문자 인덱스 = 바이트
 *  오프셋**이다(④ 가 스트림 조각을 바이트로 되집을 때 이 성질을 쓴다). */
function latin1(bytes: Uint8Array): string {
  return new TextDecoder('latin1').decode(bytes);
}

/** i 를 품은 사전(`<< … >>`)의 원문. 못 찾으면 null.
 *
 *  ★ 왜 "±N자 창"이 아닌가 — 객체 스트림 안에서는 사전들이 붙어 있어
 *    창이 **이웃 객체의 /Count 를 주워 온다**(math1.pdf 실측: 31쪽을 6으로
 *    읽었다). 여닫이(`<<`/`>>`)를 짝 맞춰 경계를 정확히 자른다. */
function dictAround(s: string, i: number): string | null {
  // 뒤로 — 짝이 맞은 형제 사전(<<…>>)은 통째로 건너뛰며 여는 << 를 찾는다.
  let depth = 0;
  let start = -1;
  for (let p = i; p >= 1 && p > i - 8000; p--) {
    if (s.charCodeAt(p) === 62 && s.charCodeAt(p - 1) === 62) {       // >>
      depth += 1; p -= 1;
    } else if (s.charCodeAt(p) === 60 && s.charCodeAt(p - 1) === 60) { // <<
      if (depth === 0) { start = p - 1; break; }
      depth -= 1; p -= 1;
    }
  }
  if (start < 0) return null;
  // 앞으로 — 짝 맞는 >> 까지.
  depth = 0;
  for (let p = start; p < s.length - 1 && p < start + 16000; p++) {
    if (s.charCodeAt(p) === 60 && s.charCodeAt(p + 1) === 60) {       // <<
      depth += 1; p += 1;
    } else if (s.charCodeAt(p) === 62 && s.charCodeAt(p + 1) === 62) { // >>
      depth -= 1; p += 1;
      if (depth === 0) return s.slice(start, p + 1);
    }
  }
  return null;
}

/** 훑기 한 벌 — /Pages 의 /Count 최댓값과 /Page 낱장 수. ①·③·④ 공용. */
function scanCounts(s: string): { best: number; pages: number } {
  let best = 0;
  const pagesRe = /\/Type\s*\/Pages\b/g;
  let m: RegExpExecArray | null;
  while ((m = pagesRe.exec(s))) {
    // /Count 는 **같은 사전 안**에서만 — 경계를 못 찾으면 틀린 숫자 대신 무시.
    const d = dictAround(s, m.index);
    const c = d && /\/Count\s+(\d+)/.exec(d);
    if (c) best = Math.max(best, parseInt(c[1], 10));
  }
  const pageRe = /\/Type\s*\/Page\b/g;
  let pages = 0;
  while (pageRe.exec(s)) pages += 1;
  return { best, pages };
}

/** zlib(deflate) 한 조각을 브라우저 내장 해제기로 푼다. 못 풀면 null. */
async function inflate(chunk: Uint8Array): Promise<Uint8Array | null> {
  try {
    const ds = new DecompressionStream('deflate');
    const resp = new Response(new Blob([chunk as BlobPart]).stream().pipeThrough(ds));
    return new Uint8Array(await resp.arrayBuffer());
  } catch {
    return null;
  }
}

/**
 * PDF 쪽수. **null = 모름**(틀린 숫자를 돌려주지 않는다).
 * 차감 정본은 서버 pypdf — 이 값은 확인 단계 표시용이다.
 */
export async function pdfPageCount(bytes: Uint8Array): Promise<number | null> {
  if (!bytes || bytes.length < 8) return null;
  // 헤더는 파일 앞 1024바이트 안에 있어야 한다(스펙 허용 범위).
  if (latin1(bytes.subarray(0, 1024)).indexOf('%PDF-') < 0) return null;
  const s = latin1(bytes);

  // ① 평문 페이지 트리
  const flat = scanCounts(s);
  let best = flat.best;

  // ② 선형화 사전의 /N (전체 쪽수)
  const lin = /\/Linearized\s+[\d.]+/.exec(s);
  if (lin) {
    const end = s.indexOf('>>', lin.index);
    const win = s.slice(lin.index, end >= 0 ? end + 2 : lin.index + 400);
    const n = /\/N\s+(\d+)/.exec(win);
    if (n) best = Math.max(best, parseInt(n[1], 10));
  }
  if (best > 0) return best;
  if (flat.pages > 0) return flat.pages;         // ③ 낱장 세기

  // ④ 압축 객체 스트림 — 페이지 트리가 통째로 이 안에 있는 PDF(1.5+).
  if (typeof DecompressionStream === 'undefined') return null;
  let pages = 0;
  const objstmRe = /\/Type\s*\/ObjStm\b/g;
  let m: RegExpExecArray | null;
  while ((m = objstmRe.exec(s))) {
    const kw = s.indexOf('stream', m.index);
    if (kw < 0) continue;
    // 데이터는 'stream' 키워드 뒤 EOL(\r\n 또는 \n) 다음 바이트부터.
    let start = kw + 6;
    if (s.charCodeAt(start) === 13) start += 1;   // \r
    if (s.charCodeAt(start) === 10) start += 1;   // \n
    let end = s.indexOf('endstream', start);
    if (end < 0) continue;
    // 꼬리 공백은 zlib 뒤에 붙은 군더더기다 — 해제기가 거절할 수 있어 걷는다.
    while (end > start && /[\r\n \t]/.test(s[end - 1])) end -= 1;
    const inner = await inflate(bytes.subarray(start, end));
    if (!inner) continue;                          // Flate 가 아니거나 손상 — 건너뜀
    const got = scanCounts(latin1(inner));
    best = Math.max(best, got.best);
    pages += got.pages;
  }
  if (best > 0) return best;
  return pages > 0 ? pages : null;
}

/**
 * ★★★★★★ F929 B-4(사장님 2026-10-01 «하자») — **파일 앞 조각만으로** 쪽수를 아는 빠른 길.
 *
 * 왜: 다중 업로드는 파일마다 쪽수를 세는데, 옛 판은 **파일 전체**를 메모리에 올렸다(64MB × 동시 3개
 * = 탭 메모리 수백 MB — 휴대폰에서 탭이 죽을 수 있다).
 * 무엇: 선형화(«빠른 웹 보기») PDF 는 **첫 객체**(스펙: 첫 1024바이트 안)의 선형화 사전이 전체 쪽수
 * `/N` 과 파일 길이 `/L` 을 말한다. ★ `/L` 이 실제 파일 크기와 **같을 때만** 믿는다 — 증분 저장으로
 * 뒤에 덧붙은 파일은 선형화가 깨져 `/N` 이 낡았을 수 있다(그때는 null → 전체 판독으로).
 * 못 믿으면 **null**(틀린 숫자를 돌려주지 않는다 — 이 파일의 규칙 그대로).
 */
export function pdfPageCountHead(head: Uint8Array, fileSize: number): number | null {
  if (!head || head.length < 8) return null;
  const s = latin1(head.subarray(0, Math.min(head.length, 4096)));
  if (s.slice(0, 1024).indexOf('%PDF-') < 0) return null;
  const lin = /\/Linearized\s+[\d.]+/.exec(s);
  if (!lin || lin.index > 1024) return null;
  const end = s.indexOf('>>', lin.index);
  if (end < 0) return null;
  const win = s.slice(lin.index, end + 2);
  const n = /\/N\s+(\d+)/.exec(win);
  const l = /\/L\s+(\d+)/.exec(win);
  if (!n || !l) return null;
  if (parseInt(l[1], 10) !== Math.round(fileSize)) return null;
  const pages = parseInt(n[1], 10);
  return pages > 0 ? pages : null;
}
